import { ClearBaseIcon } from '@tableau/icons/ClearBaseIcon';
import { ExcludeBaseIcon } from '@tableau/icons/ExcludeBaseIcon';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, CompactInlineAlertWidget, DialogType, InlineAlertType, InlineAlertWidget, PopoverWidget, TextLinkWidget, Tooltip } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { useOnSettingsRevert } from '~settings-coordinator/SettingsCoordinatorHook';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { SessionService } from '~src/commonInfra/services/SessionService';
import { AddOAuthClientParams } from '~src/connectivity/serverApi/requests/OAuth/AddOAuthClientsRequest';
import { IOAuthClient } from '~src/connectivity/serverApi/types/OAuth/IOAuthClient';
import { OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { connectToLLMDialogStyle } from '~src/settings/general-settings/styles/tableau-gai/ConnectToLLMRegistryStyles';
import { GenerativeAIOnPremSectionStyles } from '~src/settings/general-settings/styles/tableau-gai/GenerativeAIOnPremSectionStyles';
import { TableauGaiSharedStyles } from '~src/settings/general-settings/styles/tableau-gai/TableauGaiSharedStyles';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { SessionServerSelectors } from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { addOAuthClients, deleteOAuthClients, fetchOAuthClients } from '../oauth-clients-registry/OAuthClientsRegistry';
import { AgentPermissionsTable } from '../tableau-ai-agents/AgentPermissionsTable';
import { Agent, AgentId } from '../tableau-ai-agents/AgentSectionTypes';
import { analyticsAssistantFetchWithOAuthRetry, HARDCODED_LLM_GATEWAY_OAUTH_CONFIG_ID } from './analyticsAssistantFetch';
import { ConnectToLLMDialog, ConnectToLLMDialogProps, ConnectToLLMSetting } from './ConnectToLLMDialog';
import { ErrorDetail, openErrorDetailsModal } from './ErrorDetailsModal';
import {
  AUTH_MODES,
  DEFAULT_MODELS,
  GAI_EMBEDDINGS_MODEL_SITE_SETTING,
  GAI_LLM_AUTH_MODE,
  GAI_LLM_OAUTH_CONFIG_ID,
  GAI_QUERY_PARSING_MODEL_SITE_SETTING,
  GAI_VIZ_AND_CALC_GENERATION_MODEL_SITE_SETTING,
  PROVIDER_TYPES,
} from './GenerativeAITypes';
import { ModelSettingsSection } from './ModelSettingsSection';
import { buildSelectModelsDialogProps, ModelSelection, SelectModelsDialog, SelectModelsDialogProps } from './SelectModelsDialog';

export enum GAIFeature {
  Authoring,
  DashboardCabu,
  DashboardNarratives,
  Prep,
  Catalog,
  AiSearch,
}

const GAI_FEATURE_FIELD_NAMES: Record<GAIFeature, string> = {
  [GAIFeature.Authoring]: 'generativeAiWebAuthoringOnPremEnabled',
  [GAIFeature.DashboardCabu]: 'dashboardQAEnabled',
  [GAIFeature.DashboardNarratives]: 'dashboardNarrativeEnabled',
  [GAIFeature.Prep]: 'generativeAiPrep',
  [GAIFeature.Catalog]: 'generativeAiDataCatalog',
  [GAIFeature.AiSearch]: 'tableauAiSearchEnabled',
};

// Features whose setting lives in the V2 KV bag (settings.siteSettingsV2.<key>) rather than as a
// typed top-level field (settings.<key>). DashboardCabu and AiSearch are both siteSettingsV2-registered.
const SITE_SETTINGS_V2_FEATURES: ReadonlySet<GAIFeature> = new Set([
  GAIFeature.DashboardCabu,
  GAIFeature.AiSearch,
]);

function getGAIFeatureSettingsPath(feature: GAIFeature): string {
  return SITE_SETTINGS_V2_FEATURES.has(feature)
    ? `settings.siteSettingsV2.${GAI_FEATURE_FIELD_NAMES[feature]}`
    : `settings.${GAI_FEATURE_FIELD_NAMES[feature]}`;
}

export const GAI_MODELS_REQUEST_KEY = 'key';
export const GAI_MODELS_REQUEST_AZURE_RESOURCE_NAME = 'azureAIResourceName';

export const GAI_LLM_PROVIDER_API_KEY = 'settings.generativeAiLLMProviderAPIKey';
export const GAI_DEFAULT_API_KEY_PLACEHOLDER = 'generativeAiLLMProviderAPIKeySet';

export const GAI_LLM_PROVIDER = 'settings.siteSettingsV2.onPremLLMProvider';
export const GAI_AZURE_AI_RESOURCE_NAME = 'settings.siteSettingsV2.azureAIResourceName';
export const GAI_AZURE_AI_SUBSCRIPTION_ID = 'settings.siteSettingsV2.azureAISubscriptionID';
export const GAI_AZURE_AI_RESOURCE_GROUP = 'settings.siteSettingsV2.azureAIResourceGroup';
export const GAI_AZURE_AI_TENANT_ID = 'settings.siteSettingsV2.azureAITenantId';
export const GAI_HOST_URL = 'settings.siteSettingsV2.hostURL';

export type GenerativeAISectionOnPremProps = SiteSettingsForSiteAdminProps<
  | 'generativeAiWebAuthoringOnPremEnabled'
  | 'generativeAiPrep'
  | 'generativeAiDataCatalog'
  | 'dashboardNarrativeEnabled'
  | 'generativeAiLLMProviderAPIKey'
  | 'siteSettingsV2'
>;

export type ModelTestKey = 'queryParsing' | 'vizAndCalc' | 'embeddings';

export interface ModelTestResult {
  key: ModelTestKey;
  modelLabel: string;
  success: boolean;
  errorDetail?: ErrorDetail;
}

// Cap raw HTTP response bodies so a large payload (e.g. a multi-MB HTML
// error page from an upstream proxy) cannot lock the renderer when we
// display it as pre-wrap text in the error details dialog.
const RAW_RESPONSE_MAX_LENGTH = 2048;

const SECRET_REDACTION_PATTERNS: RegExp[] = [
  // Authorization / Bearer tokens in headers or JSON
  /(authorization["'\s:=]+)(?:bearer\s+)?[A-Za-z0-9._\-+/=]{8,}/gi,
  /(bearer\s+)[A-Za-z0-9._\-+/=]{8,}/gi,
  // OpenAI-style secret keys (sk-…, sk-proj-…)
  /\bsk-(?:proj-)?[A-Za-z0-9_\-]{16,}\b/g,
  // Generic api_key / apikey / apiKey / api-key / access_token / secret / password assignments
  /((?:api[_\-]?key|access[_\-]?token|secret|password)["'\s:=]+)["']?[A-Za-z0-9._\-+/=]{8,}["']?/gi,
  // Bare "key" field (the literal name our /test-models body uses for the credential).
  // Anchored to a JSON/header-style boundary (start-of-string, whitespace, brace, comma,
  // or quote) so legitimate words ending in "key" — e.g. monkey, lowkey — aren't redacted.
  /((?:^|[\s,{"'])key["'\s:=]+)["']?[A-Za-z0-9._\-+/=]{8,}["']?/gi,
  // x-api-key / azure-api-key header style
  /((?:x-api-key|azure-api-key|ocp-apim-subscription-key)["'\s:=]+)["']?[A-Za-z0-9._\-+/=]{8,}["']?/gi,
];

export function redactSecrets(text: string): string {
  let redacted = text;
  for (const pattern of SECRET_REDACTION_PATTERNS) {
    redacted = redacted.replace(pattern, (...args: unknown[]) => {
      // String.prototype.replace invokes the callback as
      //   (match, ...captureGroups, offset, fullString[, namedGroups])
      // For patterns without a capture group, args[1] is the numeric
      // offset; for patterns with one leading capture group, args[1] is
      // the prefix we want to preserve. Only keep it when it's a string.
      const prefix = typeof args[1] === 'string' ? args[1] : '';
      return prefix + '[REDACTED]';
    });
  }
  return redacted;
}

function truncateRawResponse(text: string): string {
  const safe = redactSecrets(text);
  if (safe.length <= RAW_RESPONSE_MAX_LENGTH) {
    return safe;
  }
  return safe.slice(0, RAW_RESPONSE_MAX_LENGTH) + '\n…[truncated]';
}

// Scrubs and caps a single-line error summary that originates from an
// upstream provider response. Provider-supplied messages can echo back
// credentials or span many kilobytes, so run the same redaction +
// length cap used for the raw response body.
function sanitizeErrorMessage(message: string): string {
  return redactSecrets(message).slice(0, RAW_RESPONSE_MAX_LENGTH);
}

export function buildErrorDetail(args: {
  message: string;
  provider: string;
  response?: { status: number, statusText: string };
  rawResponse?: string;
}): ErrorDetail {
  return {
    message: args.message,
    statusCode: args.response?.status,
    statusText: args.response?.statusText,
    provider: args.provider,
    timestamp: new Date().toISOString(),
    rawResponse: args.rawResponse,
  };
}

type AlertMessage = {
  type: InlineAlertType | null;
  text: string;
  dismissible: boolean;
  errorDetail?: ErrorDetail;
};

const emptyAlertMessage = (): AlertMessage => {
  return {
    type: null,
    text: '',
    dismissible: true,
  };
};

const connectionSuccessMessage = (): AlertMessage => {
  return {
    type: InlineAlertType.Success,
    text: Messages.onPremGAISiteSettingsConnectionSuccessMessage(),
    dismissible: true,
  };
};

const connectionErrorMessage = (errorDetail?: ErrorDetail): AlertMessage => {
  return {
    type: InlineAlertType.Error,
    text: Messages.onPremGAISiteSettingsConnectionFailedShortMessage(),
    dismissible: false,
    errorDetail,
  };
};

const oauthClientSaveFailedMessage = (errorDetail?: ErrorDetail): AlertMessage => {
  return {
    type: InlineAlertType.Error,
    text: Messages.onPremGAIOAuthClientSaveFailedMessage(),
    dismissible: false,
    errorDetail,
  };
};

const oauthClientDeleteFailedMessage = (errorDetail?: ErrorDetail): AlertMessage => {
  return {
    type: InlineAlertType.Error,
    text: Messages.onPremGAIOAuthClientDeleteFailedMessage(),
    dismissible: false,
    errorDetail,
  };
};

const specifyModelsWarningMessage = (): AlertMessage => {
  return {
    type: InlineAlertType.Warning,
    text: Messages.onPremGAISiteSettingsSpecifyModelsWarningMessage(),
    dismissible: true,
  };
};

const connectionDeletedMessage = (): AlertMessage => {
  return {
    type: InlineAlertType.Success,
    text: Messages.onPremGAISiteSettingsConnectionDeletedMessage(),
    dismissible: true,
  };
};

function buildProviderFields(
  provider: string,
  apiKey: string,
  azureResourceName?: string,
  azureSubscriptionId?: string,
  azureResourceGroup?: string,
  azureTenantId?: string,
  hostURL?: string,
  authMode?: string,
  oauthConfigId?: string
): Record<string, any> {
  const resolvedAPIKey = apiKey === GAI_DEFAULT_API_KEY_PLACEHOLDER ? '' : apiKey;

  const fields: Record<string, any> = {
    key: resolvedAPIKey,
  };

  switch (provider) {
    case PROVIDER_TYPES.OPENAI:
      break;
    case PROVIDER_TYPES.AZURE_OPENAI:
      fields.azureAIResourceName = azureResourceName;
      if (FeatureFlags.azureAiOnPremEnabledSP2) {
        fields.azureAISubscriptionID = azureSubscriptionId;
        fields.azureAIResourceGroup = azureResourceGroup;
        fields.azureAITenantId = azureTenantId;
      }
      break;
    case PROVIDER_TYPES.BRING_YOUR_OWN_LLM:
      fields.hostURL = hostURL;
      // Always forward authMode / oauthConfigId so the backend knows the auth choice.
      fields.authMode = authMode || '';
      fields.oauthConfigId = oauthConfigId || '';
      break;
    default:
      fields.key = 'noKnownProvider';
  }
  return fields;
}

async function queryOpenAIModels(
  provider: string,
  parameters: Record<string, any>,
  onAuthRequired?: () => void
): Promise<{ models: string[], errorDetail?: ErrorDetail }> {
  try {
    const url = '/content-exploration/analytics-assistant/models';
    const response = await analyticsAssistantFetchWithOAuthRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': SessionService.getSessionId(),
      },
      body: JSON.stringify({
        provider: `${provider}`,
        parameters: parameters,
      }),
    }, { provider, onAuthRequired });

    if (!response.ok) {
      let errorMessage = Messages.onPremGAIErrorFetchModelsFailed();
      let rawResponse = '';
      try {
        const responseText = await response.text();
        rawResponse = truncateRawResponse(responseText);
        const errorData = JSON.parse(responseText);
        errorMessage = errorData?.message || errorData?.error || errorMessage;
      } catch {
        // Non-JSON response (e.g. HTML error page). Fall back to the raw body
        // so something diagnostic appears in the alert.
        errorMessage = rawResponse || errorMessage;
      }

      return {
        models: [],
        errorDetail: buildErrorDetail({
          message: sanitizeErrorMessage(errorMessage),
          provider: getProviderDisplayName(provider),
          response,
          rawResponse,
        }),
      };
    }

    const data = await response.json();
    return { models: Array.isArray(data?.models) ? data.models : [] };
  } catch (error) {
    return {
      models: [],
      errorDetail: buildErrorDetail({
        message: error instanceof Error ? error.message : Messages.onPremGAIErrorNetworkFailure(),
        provider: getProviderDisplayName(provider),
      }),
    };
  }
}

async function testModel(
  provider: string,
  modelName: string,
  usage = 'chat',
  parameters: Record<string, any> = {},
  onAuthRequired?: () => void
): Promise<Response> {
  const baseUrl = provider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM ? parameters.hostURL || '' : '';

  const testParameters: Record<string, any> = {
    apiKey: parameters.key || '',
    hostURL: baseUrl,
  };

  if (provider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
    // Forward auth-mode site settings so the backend picks the right validator.
    testParameters.authMode = parameters.authMode || '';
    testParameters.oauthConfigId = parameters.oauthConfigId || '';
  }

  if (provider === PROVIDER_TYPES.AZURE_OPENAI) {
    if (parameters.azureAIResourceName) {
      testParameters.azureAIResourceName = parameters.azureAIResourceName;
    }
    if (FeatureFlags.azureAiOnPremEnabledSP2) {
      if (parameters.azureAISubscriptionID) {
        testParameters.azureAISubscriptionID = parameters.azureAISubscriptionID;
      }
      if (parameters.azureAIResourceGroup) {
        testParameters.azureAIResourceGroup = parameters.azureAIResourceGroup;
      }
      if (parameters.azureAITenantId) {
        testParameters.azureAITenantId = parameters.azureAITenantId;
      }
    }
  }

  const url = '/content-exploration/analytics-assistant/test-models';
  const requestBody = {
    provider: provider,
    usage: usage,
    modelName: modelName,
    parameters: testParameters,
  };

  return analyticsAssistantFetchWithOAuthRetry(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/vnd.analytics-assistant.test-models.v1.0+json',
      'X-XSRF-TOKEN': SessionService.getSessionId(),
    },
    body: JSON.stringify(requestBody),
  }, { provider, onAuthRequired });
}

function getProviderDisplayName(provider: string): string {
  switch (provider) {
    case PROVIDER_TYPES.OPENAI:
      return Messages.connectToLLMDialogProviderOpenAI();
    case PROVIDER_TYPES.AZURE_OPENAI:
      return Messages.connectToLLMDialogProviderAzureOpenAI();
    case PROVIDER_TYPES.BRING_YOUR_OWN_LLM:
      return Messages.connectToLLMDialogProviderBringYourOwnLLM();
    case PROVIDER_TYPES.SELECT_PROVIDER:
    default:
      return Messages.connectToLLMDialogProviderSelectDefault();
  }
}

export async function testModelWithErrorDetail(
  provider: string,
  modelName: string,
  usage: 'chat' | 'embeddings',
  parameters: Record<string, any>,
  onAuthRequired?: () => void
): Promise<{ success: boolean, errorDetail?: ErrorDetail }> {
  try {
    const response = await testModel(provider, modelName, usage, parameters, onAuthRequired);

    let rawResponse = '';
    try {
      const responseText = await response.text();
      rawResponse = truncateRawResponse(responseText);
      const responseData = JSON.parse(responseText);
      const httpCode = responseData?.httpCode;
      const upstreamFailed = typeof httpCode === 'number' && (httpCode < 200 || httpCode >= 300);

      if (httpCode === 404) {
        return {
          success: false,
          errorDetail: buildErrorDetail({
            message: sanitizeErrorMessage(responseData?.message || Messages.onPremGAIErrorChatCompletionsNotFound()),
            provider: getProviderDisplayName(provider),
            response: { status: 404, statusText: 'Not Found' },
            rawResponse,
          }),
        };
      }

      if (!response.ok || upstreamFailed) {
        const status = typeof httpCode === 'number' ? httpCode : response.status;
        return {
          success: false,
          errorDetail: buildErrorDetail({
            message: sanitizeErrorMessage(responseData?.message || responseData?.error || Messages.onPremGAIErrorConnectionFailed()),
            provider: getProviderDisplayName(provider),
            response: { status, statusText: response.statusText },
            rawResponse,
          }),
        };
      }

      return { success: true };
    } catch {
      return {
        success: false,
        errorDetail: buildErrorDetail({
          message: Messages.onPremGAIErrorParseResponseFailed(),
          provider: getProviderDisplayName(provider),
          response,
          rawResponse,
        }),
      };
    }
  } catch (error) {
    return {
      success: false,
      errorDetail: buildErrorDetail({
        message: error instanceof Error ? error.message : Messages.onPremGAIErrorNetworkFailure(),
        provider: getProviderDisplayName(provider),
      }),
    };
  }
}

export function GenerativeAISectionOnPrem(props: GenerativeAISectionOnPremProps): React.ReactElement | null {
  const [isProviderReachable, setIsProviderReachable] = React.useState<boolean>(false);
  const [areAllModelsValid, setAreAllModelsValid] = React.useState<boolean>(true);
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>(emptyAlertMessage());
  const [hasEditModelsError, setHasEditModelsError] = React.useState<boolean>(false);
  const [selectModelsDialogProps, setSelectModelsDialogProps] = React.useState<SelectModelsDialogProps | null>(null);
  const [modelTestResults, setModelTestResults] = React.useState<ModelTestResult[] | null>(null);
  const [availableModels, setAvailableModels] = React.useState<string[] | null>(null);
  // Existing llm-gateway OAuth client, used to prefill the Edit LLM dialog. Re-fetched after save.
  const [existingLlmGatewayClient, setExistingLlmGatewayClient] = React.useState<IOAuthClient | null>(null);
  // Set when a BYOLLM 401 is observed and OAuth preconditions are met; drives the sign-in alert.
  const [needsOAuthSignIn, setNeedsOAuthSignIn] = React.useState<boolean>(false);
  // True while a connection test is in flight — shows a spinner on the Test button and blocks re-clicks.
  const [isTestingConnection, setIsTestingConnection] = React.useState<boolean>(false);

  // Reverting the settings page only resets Formik values; the alert / model-test / sign-in state and the
  // connection-validity flags above live in local React state, so reset them to their initial-mount values
  // here — otherwise a stale error would linger, and the toggle-gating flags would stay out of sync with the
  // reverted config (the initial-load connection test only re-runs on [isOnlineEnvironment], not on revert).
  useOnSettingsRevert('GenerativeAISectionOnPrem', () => {
    setAlertMessage(emptyAlertMessage());
    setModelTestResults(null);
    setAvailableModels(null);
    setHasEditModelsError(false);
    setNeedsOAuthSignIn(false);
    setIsProviderReachable(false);
    setAreAllModelsValid(true);
  });

  const handleAuthRequired = React.useCallback(() => {
    setNeedsOAuthSignIn(true);
  }, []);

  // Returns a callback that flips a local flag and forwards to handleAuthRequired.
  // Lets call sites distinguish "this run hit a 401" from generic failures so the
  // OAuth sign-in alert is the only error UI shown for that case.
  const makeAuthRequiredFlag = () => {
    const flag = { fired: false };
    const callback = () => {
      flag.fired = true;
      handleAuthRequired();
    };
    return { flag, callback };
  };

  const formik = useFormikContext<any>();

  const queryParsingModel = formik.values.settings.siteSettingsV2?.generativeAiQueryParsingModel || '';
  const vizAndCalcGenerationModel = formik.values.settings.siteSettingsV2?.generativeAiVizAndCalcGenerationModel || '';
  const embeddingsModel = formik.values.settings.siteSettingsV2?.generativeAiEmbeddingsModel || '';

  const buildParametersFromFormikValues = (provider: string): Record<string, any> => {
    return buildProviderFields(
      provider,
      formik.values.settings.generativeAiLLMProviderAPIKey || '',
      formik.values.settings.siteSettingsV2?.azureAIResourceName || '',
      formik.values.settings.siteSettingsV2?.azureAISubscriptionID || '',
      formik.values.settings.siteSettingsV2?.azureAIResourceGroup || '',
      formik.values.settings.siteSettingsV2?.azureAITenantId || '',
      formik.values.settings.siteSettingsV2?.hostURL || '',
      formik.values.settings.siteSettingsV2?.onPremLLMAuthMode || AUTH_MODES.NONE,
      formik.values.settings.siteSettingsV2?.onPremLLMOAuthConfigId || ''
    );
  };

  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isTableauAgentDashboardLicensed = useSelector(SessionServerSelectors.tableauAgentDashboardLicensed);

  // Fetch the existing llm-gateway OAuth client (if any). Used to pre-populate the
  // Edit LLM Connection dialog so admins can see what's already configured.
  const refreshExistingLlmGatewayClient = React.useCallback(async (): Promise<IOAuthClient | null> => {
    if (!FeatureFlags.analyticsAssistantBYOLLMOAuthEnabled) {
      return null;
    }
    try {
      const result = await fetchOAuthClients();
      const client = result.oauthClients?.find((c) => c.type === OAuthCredentialTypes.LlmGateway) ?? null;
      setExistingLlmGatewayClient(client);
      return client;
    } catch {
      // Best-effort prefill — fall through with no prefill.
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (isOnlineEnvironment) {
      return;
    }
    refreshExistingLlmGatewayClient();
  }, [isOnlineEnvironment, refreshExistingLlmGatewayClient]);

  // Tableau Agent in Dashboards (DashboardCabu) is not supported when the Custom
  // Gateway (BYOLLM) is authenticated via OAuth. Keep the setting off whenever that
  // combination is active so a previously-enabled feature can't linger.
  // An explicitly-selected auth mode always wins: switching to None/ApiKey re-enables
  // the feature even if a previously-saved OAuth client is still on the server. The
  // existingLlmGatewayClient fallback only applies before an auth mode is chosen.
  const onPremLLMAuthMode = formik.values.settings.siteSettingsV2?.onPremLLMAuthMode;
  const isByollmOAuth =
    formik.values.settings.siteSettingsV2?.onPremLLMProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM &&
    (onPremLLMAuthMode === AUTH_MODES.OAUTH
      || (!onPremLLMAuthMode && !!existingLlmGatewayClient));

  const dashboardQAEnabled = formik.values.settings.siteSettingsV2?.dashboardQAEnabled;
  React.useEffect(() => {
    if (isOnlineEnvironment) {
      return;
    }
    if (isByollmOAuth && dashboardQAEnabled) {
      formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.DashboardCabu), false);
    }
    // `formik` is intentionally omitted: useFormikContext returns a new object each render, so
    // listing it would re-run this effect every render. isByollmOAuth captures the provider/auth-mode
    // inputs; dashboardQAEnabled is listed so the revert also fires when the permissions table turns
    // the feature on while OAuth is already active (the flag flips without isByollmOAuth changing).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlineEnvironment, isByollmOAuth, dashboardQAEnabled]);

  React.useEffect(() => {
    if (isOnlineEnvironment) {
      return;
    }
    let cancelled = false;
    const init = async () => {
      setIsTestingConnection(true);
      try {
        const provider = formik.values.settings.siteSettingsV2?.onPremLLMProvider || '';
        const apiKey = formik.values.settings.generativeAiLLMProviderAPIKey || '';

        if (provider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
          await runByollmConnectionTest({ isCancelled: () => cancelled, isInitialLoad: true });
        } else {
          const parameters = buildParametersFromFormikValues(provider);
          const result = await queryOpenAIModels(provider, parameters, handleAuthRequired);
          if (cancelled) return;
          handleModelsQueryResult(result.models, result.errorDetail, { showOnlyFailureMessage: true });
          if (!apiKey) {
            dismissAlert();
          }
        }
      } catch (error) {
        if (cancelled) return;
        setHasEditModelsError(true);
        setIsProviderReachable(false);
        setAreAllModelsValid(false);
      } finally {
        // Skip the state update if the component unmounted mid-load.
        if (!cancelled) {
          setIsTestingConnection(false);
        }
      }
    };

    init();
    return () => { cancelled = true; };
    // Intentionally captures initial Formik values; later edits go through setFieldValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlineEnvironment]);

  if (isOnlineEnvironment) {
    return null;
  }

  // Runs a test with the Test button disabled and its inline spinner showing, clearing stale alerts
  // first. Every test entry point (Test button, Connect and Edit-models dialogs) goes through this.
  async function withConnectionTestIndicator(run: () => Promise<void>): Promise<void> {
    setIsTestingConnection(true);
    dismissAlert();
    setModelTestResults(null);
    try {
      await run();
    } finally {
      setIsTestingConnection(false);
    }
  }

  async function runByollmConnectionTest(opts: {
    paramOverride?: Record<string, any>;
    modelOverrides?: { queryParsing?: string, vizAndCalc?: string, embeddings?: string };
    isCancelled?: () => boolean;
    isInitialLoad?: boolean;
  } = {}): Promise<void> {
    const { paramOverride, modelOverrides, isCancelled, isInitialLoad } = opts;
    if (isCancelled?.()) return;
    // Clear stale sign-in state; the auth callback re-sets it only if this run hits a 401.
    setNeedsOAuthSignIn(false);
    const queryParsing = modelOverrides?.queryParsing ?? queryParsingModel;
    const vizAndCalc = modelOverrides?.vizAndCalc ?? vizAndCalcGenerationModel;
    const embeddings = modelOverrides?.embeddings ?? embeddingsModel;
    const hasModels = queryParsing.trim().length > 0
      || vizAndCalc.trim().length > 0
      || embeddings.trim().length > 0;
    if (!hasModels) {
      setIsProviderReachable(false);
      setAreAllModelsValid(false);
      setAlertMessage(specifyModelsWarningMessage());
      setModelTestResults(null);
      if (!isInitialLoad) {
        disableAllFeatures();
      }
      return;
    }
    const parameters = paramOverride
      ?? buildParametersFromFormikValues(PROVIDER_TYPES.BRING_YOUR_OWN_LLM);
    const { flag, callback } = makeAuthRequiredFlag();
    const results = await testSelectedModels(PROVIDER_TYPES.BRING_YOUR_OWN_LLM, parameters, {
      queryParsing, vizAndCalc, embeddings,
    }, callback);
    if (isCancelled?.()) return;
    const allPassed = results.every((result) => result.success);
    // BYOLLM has no separate /models call — per-model results drive both flags.
    setIsProviderReachable(allPassed);
    setAreAllModelsValid(allPassed);
    if (allPassed) {
      setAlertMessage(connectionSuccessMessage());
      // Keep the results so the "Model settings valid" confirmation renders,
      // matching the OpenAI/Azure success path.
      setModelTestResults(results);
    } else if (flag.fired) {
      // OAuth sign-in alert covers this — skip the per-model error list.
      setAlertMessage(emptyAlertMessage());
      setModelTestResults(null);
      if (!isInitialLoad) {
        disableAllFeatures();
      }
    } else {
      setAlertMessage(connectionErrorMessage());
      setModelTestResults(results);
      if (!isInitialLoad) {
        disableAllFeatures();
      }
    }
  }

  async function testConnection(provider: string, apiKey?: string, parameters?: Record<string, any>): Promise<{ success: boolean }> {
    if (apiKey && (provider === PROVIDER_TYPES.OPENAI || provider === PROVIDER_TYPES.AZURE_OPENAI)) {
      // Suppress the generic connection-error when the OAuth sign-in alert covers it.
      const { flag, callback } = makeAuthRequiredFlag();
      const result = await queryOpenAIModels(provider, parameters ?? buildParametersFromFormikValues(provider), callback);
      handleModelsQueryResult(result.models, result.errorDetail, { suppressFailureMessage: flag.fired });
      const success = result.models.length > 0;
      if (!success) {
        disableAllFeatures();
      }
      return { success };
    } else {
      setAlertMessage(connectionErrorMessage());
      disableAllFeatures();
      return { success: false };
    }
  }

  async function runOpenAIOrAzureTest(
    provider: string,
    apiKey: string,
    modelOverrides?: { queryParsing?: string, vizAndCalc?: string, embeddings?: string }
  ): Promise<void> {
    const { success } = await testConnection(provider, apiKey);
    if (!success) {
      return;
    }
    await runSelectedModelsTest(provider, modelOverrides);
  }

  async function runSelectedModelsTest(
    provider: string,
    modelOverrides?: { queryParsing?: string, vizAndCalc?: string, embeddings?: string }
  ): Promise<void> {
    const parameters = buildParametersFromFormikValues(provider);
    const { flag, callback } = makeAuthRequiredFlag();
    const results = await testSelectedModels(provider, parameters, modelOverrides, callback);
    const allModelsValid = results.length === 0 || results.every((result) => result.success);
    setAreAllModelsValid(allModelsValid);
    if (!allModelsValid) {
      // OAuth sign-in alert covers the failure when flag fired; skip per-model list.
      setModelTestResults(flag.fired ? null : results);
      disableAllFeatures();
    } else {
      setModelTestResults(results);
    }
  }

  async function testSelectedModels(
    provider: string,
    parameters: Record<string, any>,
    overrides?: { queryParsing?: string, vizAndCalc?: string, embeddings?: string },
    onAuthRequiredOverride?: () => void
  ): Promise<ModelTestResult[]> {
    const candidates = [
      {
        key: 'queryParsing' as const, modelName: overrides?.queryParsing ?? queryParsingModel,
        usage: 'chat' as const, modelLabel: Messages.gaiModelSettingsQueryParsingModelLabel()
      },
      {
        key: 'vizAndCalc' as const, modelName: overrides?.vizAndCalc ?? vizAndCalcGenerationModel,
        usage: 'chat' as const, modelLabel: Messages.gaiModelSettingsVizAndCalcGenerationModelLabel()
      },
      {
        key: 'embeddings' as const, modelName: overrides?.embeddings ?? embeddingsModel,
        usage: 'embeddings' as const, modelLabel: Messages.gaiModelSettingsEmbeddingsModelLabel()
      },
    ];

    const toRun = candidates.filter((candidate) => candidate.modelName.trim().length > 0);
    if (toRun.length === 0) {
      return [];
    }

    const authCallback = onAuthRequiredOverride ?? handleAuthRequired;
    return Promise.all(toRun.map(async (candidate): Promise<ModelTestResult> => {
      const result = await testModelWithErrorDetail(provider, candidate.modelName, candidate.usage, parameters, authCallback);
      return { key: candidate.key, modelLabel: candidate.modelLabel, success: result.success, errorDetail: result.errorDetail };
    }));
  }

  function handleModelsQueryResult(
    models: string[],
    errorDetail?: ErrorDetail,
    options: { showOnlyFailureMessage?: boolean, suppressFailureMessage?: boolean } = {}
  ): void {
    // OpenAI / Azure flows derive provider reachability from /models — we don't
    // run the per-model test loop here, so leave areAllModelsValid as-is (vacuous
    // true on first reach, or whatever the most recent test set it to).
    setIsProviderReachable(models.length > 0);
    setAvailableModels(models.length > 0 ? models : null);
    if (models.length > 0) {
      setHasEditModelsError(false);
      if (!options.showOnlyFailureMessage) {
        setAlertMessage(connectionSuccessMessage());
      }
    } else if (options.suppressFailureMessage) {
      // The OAuth sign-in warning alert covers this failure — don't duplicate it.
      setAlertMessage(emptyAlertMessage());
    } else {
      setAlertMessage(connectionErrorMessage(errorDetail));
    }
  }

  async function handleConnectToLLM(connectToLLMSetting: ConnectToLLMSetting): Promise<void> {
    const previousProvider = formik.values.settings.siteSettingsV2?.onPremLLMProvider || '';
    const providerChanged = previousProvider !== connectToLLMSetting.llmProvider;
    // A true switch means there was already a (different) provider configured.
    // A first-time connect (no previous provider) is not a switch, so any
    // pre-existing models are kept and tested.
    const switchedProvider = !!previousProvider && providerChanged;

    formik.setFieldValue(GAI_LLM_PROVIDER, connectToLLMSetting.llmProvider);
    formik.setFieldValue(GAI_LLM_PROVIDER_API_KEY, connectToLLMSetting.providerAPIKey || '');
    formik.setFieldValue(GAI_AZURE_AI_RESOURCE_NAME, connectToLLMSetting.azureResourceName || '');
    formik.setFieldValue(GAI_AZURE_AI_SUBSCRIPTION_ID, connectToLLMSetting.azureSubscriptionId || '');
    formik.setFieldValue(GAI_AZURE_AI_RESOURCE_GROUP, connectToLLMSetting.azureResourceGroup || '');
    formik.setFieldValue(GAI_AZURE_AI_TENANT_ID, connectToLLMSetting.azureTenantId || '');
    formik.setFieldValue(GAI_HOST_URL, connectToLLMSetting.hostURL || '');
    formik.setFieldValue(GAI_LLM_AUTH_MODE, connectToLLMSetting.authMode || AUTH_MODES.NONE);
    formik.setFieldValue(GAI_LLM_OAUTH_CONFIG_ID, connectToLLMSetting.oauthConfigId || '');

    const parameters = buildProviderFields(
      connectToLLMSetting.llmProvider,
      connectToLLMSetting.providerAPIKey || '',
      connectToLLMSetting.azureResourceName || '',
      connectToLLMSetting.azureSubscriptionId || '',
      connectToLLMSetting.azureResourceGroup || '',
      connectToLLMSetting.azureTenantId || '',
      connectToLLMSetting.hostURL || '',
      connectToLLMSetting.authMode || AUTH_MODES.NONE,
      connectToLLMSetting.oauthConfigId || ''
    );

    // Clear any sticky OAuth sign-in alert from a previous attempt.
    setNeedsOAuthSignIn(false);

    if (connectToLLMSetting.llmProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
      if (providerChanged) {
        clearFeaturesAndModels();
      }

      // Save the OAuth client when OAuth is explicitly selected and all required fields are present.
      if (FeatureFlags.analyticsAssistantBYOLLMOAuthEnabled &&
          connectToLLMSetting.authMode === AUTH_MODES.OAUTH &&
          connectToLLMSetting.oauthClientId && connectToLLMSetting.oauthClientSecret &&
          connectToLLMSetting.oauthRedirectUri) {
        const oauthClient: AddOAuthClientParams = {
          dbClass: OAuthCredentialTypes.LlmGateway,
          instanceUrl: '',
          clientId: connectToLLMSetting.oauthClientId,
          clientSecret: connectToLLMSetting.oauthClientSecret,
          redirectUri: connectToLLMSetting.oauthRedirectUri,
          oauthConfigId: connectToLLMSetting.oauthConfigId || 'default',
          oauthConfigContent: connectToLLMSetting.oauthConfigContent || undefined,
          oauthConfigFileName: connectToLLMSetting.oauthConfigFileName || undefined,
          configLabel: connectToLLMSetting.configLabel || undefined,
        };
        try {
          await addOAuthClients([oauthClient]);
          // Keep site setting in sync so /test-models and Sign-in use the saved id.
          formik.setFieldValue(GAI_LLM_OAUTH_CONFIG_ID, oauthClient.oauthConfigId);
          await refreshExistingLlmGatewayClient();
        } catch (error) {
          const message = error instanceof Error
            ? error.message
            : (typeof error === 'string' ? error : Messages.onPremGAIOAuthClientSaveFailedMessage());
          const errorDetail = buildErrorDetail({
            message: sanitizeErrorMessage(message),
            provider: getProviderDisplayName(connectToLLMSetting.llmProvider),
          });
          setAlertMessage(oauthClientSaveFailedMessage(errorDetail));
          return;
        }
      }

      setModelTestResults(null);
      setAvailableModels(null);
      // On a provider switch the models were just cleared, but the model names
      // captured in this render's closure are still the old provider's. Pass
      // empty overrides so we don't test the new provider against stale models —
      // the admin is prompted to specify models instead.
      await runByollmConnectionTest({
        paramOverride: parameters,
        modelOverrides: switchedProvider ? { queryParsing: '', vizAndCalc: '', embeddings: '' } : undefined,
      });
    } else {
      await testConnection(connectToLLMSetting.llmProvider, connectToLLMSetting.providerAPIKey, parameters);
      clearFeaturesAndModels();
      setModelTestResults(null);
      // OpenAI/Azure don't run per-model tests; reset to vacuous-true after clearing models.
      setAreAllModelsValid(true);
    }
  }

  function openConnectToLLMModal(isEditing: boolean = false): void {
    let modalInstance: ModalInstance;
    const defaultProvider = PROVIDER_TYPES.SELECT_PROVIDER;
    const currentProvider = formik.values.settings.siteSettingsV2?.onPremLLMProvider || defaultProvider;
    const currentAPIKey = formik.values.settings.generativeAiLLMProviderAPIKey || '';
    const currentAzureResourceName = formik.values.settings.siteSettingsV2?.azureAIResourceName || '';
    const currentAzureSubscriptionId = formik.values.settings.siteSettingsV2?.azureAISubscriptionID || '';
    const currentAzureResourceGroup = formik.values.settings.siteSettingsV2?.azureAIResourceGroup || '';
    const currentAzureTenantId = formik.values.settings.siteSettingsV2?.azureAITenantId || '';
    const currentHostURL = formik.values.settings.siteSettingsV2?.hostURL || '';
    // Infer authMode when the site setting is unset: existing OAuth client → OAuth, api key → ApiKey, else None.
    let currentAuthMode = formik.values.settings.siteSettingsV2?.onPremLLMAuthMode;
    if (!currentAuthMode) {
      if (existingLlmGatewayClient) {
        currentAuthMode = AUTH_MODES.OAUTH;
      } else if (currentAPIKey) {
        currentAuthMode = AUTH_MODES.API_KEY;
      } else {
        currentAuthMode = AUTH_MODES.NONE;
      }
    }
    const currentOAuthConfigId =
      formik.values.settings.siteSettingsV2?.onPremLLMOAuthConfigId
      || existingLlmGatewayClient?.oauthConfigId
      || '';

    const dialogProps: ConnectToLLMDialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectToLLMSubmit: (formikValues: ConnectToLLMSetting) =>
        withConnectionTestIndicator(() => handleConnectToLLM(formikValues)),
      isEditing: isEditing,
      azureAiOnPremEnabledSP2: FeatureFlags.azureAiOnPremEnabledSP2,
      currentValues: {
        llmProvider: currentProvider,
        providerAPIKey: isEditing ? '' : currentAPIKey, // Clear API key when editing for security
        azureResourceName: currentAzureResourceName,
        azureSubscriptionId: currentAzureSubscriptionId,
        azureResourceGroup: currentAzureResourceGroup,
        azureTenantId: currentAzureTenantId,
        hostURL: currentHostURL,
        authMode: currentAuthMode,
        // Prefill OAuth fields from the existing client; secret is never echoed back.
        oauthClientId: existingLlmGatewayClient?.clientId || '',
        oauthClientSecret: '',
        oauthRedirectUri: '',
        oauthConfigId: currentOAuthConfigId || 'default',
        configLabel: existingLlmGatewayClient?.configLabel || '',
        oauthConfigContent: existingLlmGatewayClient?.oauthConfigContent || '',
        oauthConfigFileName: existingLlmGatewayClient?.oauthConfigFileName || '',
      },
    };

    modalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: isEditing ? Messages.connectToLLMDialogTitleEdit() : Messages.connectToLLMDialogTitleConnect(),
      dialogContent: React.createElement(ConnectToLLMDialog, dialogProps),
      testId: 'connectToLLMDialog',
      showCloseButton: true,
      dialogStyle: connectToLLMDialogStyle,
      // Re-center on height change (BYOLLM OAuth expands the form vertically).
      shouldRecenterOnUpdate: true,
    });

    modalInstance.result.catch((reason) => {
      if (reason !== 'dismissDialog' && reason !== 'cancel') {
        throw reason;
      }
    });
  }

  function openDeleteConfirmationModal(): Promise<void> {
    const dialogInstance = openReactConfirmationDialog<void, void>({
      title: Messages.deleteApiKeyConfirmationDialogTitle(),
      message: Messages.deleteApiKeyConfirmationDialogMessage(),
      actionName: Messages.deleteMessage(),
      isActionDelete: true,
      paramsForAction: undefined,
      action: async () => {
        // Delete before clearing local state so a failure leaves the admin able to retry.
        if (existingLlmGatewayClient?.id) {
          try {
            await deleteOAuthClients([existingLlmGatewayClient.id]);
            setExistingLlmGatewayClient(null);
          } catch (error) {
            const message = error instanceof Error
              ? error.message
              : (typeof error === 'string' ? error : Messages.onPremGAIOAuthClientDeleteFailedMessage());
            const errorDetail = buildErrorDetail({
              message: sanitizeErrorMessage(message),
              provider: getProviderDisplayName(PROVIDER_TYPES.BRING_YOUR_OWN_LLM),
            });
            setAlertMessage(oauthClientDeleteFailedMessage(errorDetail));
            return;
          }
        }

        formik.setFieldValue(GAI_LLM_PROVIDER, '');
        formik.setFieldValue(GAI_LLM_PROVIDER_API_KEY, '');
        formik.setFieldValue(GAI_AZURE_AI_RESOURCE_NAME, '');
        formik.setFieldValue(GAI_AZURE_AI_SUBSCRIPTION_ID, '');
        formik.setFieldValue(GAI_AZURE_AI_RESOURCE_GROUP, '');
        formik.setFieldValue(GAI_AZURE_AI_TENANT_ID, '');
        formik.setFieldValue(GAI_HOST_URL, '');
        formik.setFieldValue(GAI_LLM_AUTH_MODE, AUTH_MODES.NONE);
        formik.setFieldValue(GAI_LLM_OAUTH_CONFIG_ID, '');

        setIsProviderReachable(false);
        setAreAllModelsValid(true);

        clearFeaturesAndModels();

        setAlertMessage(connectionDeletedMessage());

        setHasEditModelsError(false);
        setModelTestResults(null);
        setAvailableModels(null);
        setNeedsOAuthSignIn(false);
      },
      dialogWidth: TabDialogWidth.width400px,
    });

    return Promise.resolve(dialogInstance.result);
  }

  function clearFeaturesAndModels(): void {
    disableAllFeatures();

    formik.setFieldValue(GAI_QUERY_PARSING_MODEL_SITE_SETTING, '');
    formik.setFieldValue(GAI_VIZ_AND_CALC_GENERATION_MODEL_SITE_SETTING, '');
    formik.setFieldValue(GAI_EMBEDDINGS_MODEL_SITE_SETTING, '');
  }

  function disableAllFeatures(): void {
    formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.Authoring), false);
    formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.Prep), false);
    formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.Catalog), false);
    formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.DashboardNarratives), false);
    formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.DashboardCabu), false);
    // AiSearch is LLM-dependent on-prem, so it must turn off with the rest when the provider or
    // models are lost; otherwise AI Search would stay enabled with no working LLM behind it.
    formik.setFieldValue(getGAIFeatureSettingsPath(GAIFeature.AiSearch), false);
  }

  function clearFeature(featureToTurnOff: GAIFeature): void {
    formik.setFieldValue(getGAIFeatureSettingsPath(featureToTurnOff), false);
  }

  async function requestSelectModelsDialog(featureToEnableOnApply?: GAIFeature): Promise<void> {
    const currentProvider = formik.values.settings.siteSettingsV2?.onPremLLMProvider;
    setHasEditModelsError(false);

    if (!currentProvider || currentProvider === PROVIDER_TYPES.SELECT_PROVIDER) {
      setHasEditModelsError(true);
      return;
    }

    let models: string[] = [];
    if (currentProvider !== PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
      const result = await queryOpenAIModels(currentProvider, buildParametersFromFormikValues(currentProvider), handleAuthRequired);
      models = result.models;
      if (models.length === 0) {
        setHasEditModelsError(true);
        return;
      }
      setAvailableModels(models);
    }

    const formatInitialModelValue = (modelName: string, defaultModel: string): string => {
      if (modelName === '') {
        // For OpenAI, return the default model, for other providers return empty string
        if (currentProvider === PROVIDER_TYPES.OPENAI) {
          return defaultModel;
        }
        return '';
      }
      return modelName;
    };

    const initialQueryParsingModel = formatInitialModelValue(queryParsingModel, DEFAULT_MODELS.QUERY_PARSING);
    const initialVizAndCalcGenerationModel = formatInitialModelValue(vizAndCalcGenerationModel, DEFAULT_MODELS.VIZ_AND_CALC);
    const initialEmbeddingsModel = formatInitialModelValue(embeddingsModel, DEFAULT_MODELS.EMBEDDINGS);

    const onSubmit = (formikValues: ModelSelection) =>
      withConnectionTestIndicator(async () => {
        formik.setFieldValue(GAI_QUERY_PARSING_MODEL_SITE_SETTING, formikValues.modelQueryParsing);
        formik.setFieldValue(GAI_VIZ_AND_CALC_GENERATION_MODEL_SITE_SETTING, formikValues.modelVizAndCalcGeneration);
        formik.setFieldValue(GAI_EMBEDDINGS_MODEL_SITE_SETTING, formikValues.modelEmbeddings);

        if (featureToEnableOnApply !== undefined) {
          formik.setFieldValue(getGAIFeatureSettingsPath(featureToEnableOnApply), true);
        }

        const modelOverrides = {
          queryParsing: formikValues.modelQueryParsing,
          vizAndCalc: formikValues.modelVizAndCalcGeneration,
          embeddings: formikValues.modelEmbeddings,
        };
        if (currentProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
          await runByollmConnectionTest({ modelOverrides });
        } else {
          // The /models call already ran when this dialog was opened, so re-testing
          // the connection here would be redundant. Just exercise the selected models.
          await runSelectedModelsTest(currentProvider, modelOverrides);
        }
      });

    const closeDialog = () => setSelectModelsDialogProps(null);

    const dialogProps = buildSelectModelsDialogProps(
      currentProvider === PROVIDER_TYPES.OPENAI,
      models,
      initialQueryParsingModel,
      initialVizAndCalcGenerationModel,
      initialEmbeddingsModel,
      onSubmit,
      closeDialog,
      featureToEnableOnApply !== undefined
    );
    setSelectModelsDialogProps(dialogProps);
  }

  // Maps each permissions-table agent to its GAIFeature so the table's "turn on" reuses the
  // model-selection flow. tableauAiSearch is LLM-dependent on-prem — it requires a reachable
  // provider and valid models — so it must be mapped here; without it the table would enable
  // AI Search without requiring model selection.
  const AGENT_ID_TO_FEATURE: Partial<Record<AgentId, GAIFeature>> = {
    webAuthoring: GAIFeature.Authoring,
    dashboardInsights: GAIFeature.DashboardCabu,
    prep: GAIFeature.Prep,
    tableauAiSearch: GAIFeature.AiSearch,
  };

  // Intercepts the permissions-table "turn on": if no models are selected yet, open the Select
  // Models dialog and enable the feature only when the admin applies it (requestSelectModelsDialog
  // does the enable in its onSubmit). Returns true in that case so AgentRow does NOT also flip the
  // setting on. When models already exist, returns false so the row enables immediately.
  function handleTableRequestEnable(agent: Agent): boolean {
    const feature = AGENT_ID_TO_FEATURE[agent.id];
    if (feature === undefined) {
      return false;
    }
    const hasSelectedModels = queryParsingModel || vizAndCalcGenerationModel || embeddingsModel;
    if (hasSelectedModels) {
      return false;
    }
    requestSelectModelsDialog(feature);
    return true;
  }

  // Mirrors the standalone DashboardCabu toggle's forceDisabled: Tableau Agent in Dashboards
  // (dashboardQAEnabled) is not supported without the TableauAgentDashboard license or when the
  // Custom Gateway authenticates via OAuth, so the table must not offer "turn on" for it in those
  // states. No other on-prem feature is force-disabled this way.
  function isTableEnableForceDisabled(agent: Agent): boolean {
    if (agent.id !== 'dashboardInsights') {
      return false;
    }
    return isByollmOAuth || !isTableauAgentDashboardLicensed;
  }

  function dismissAlert(): void {
    setAlertMessage(emptyAlertMessage());
    setHasEditModelsError(false);
  }

  // Pops the OAuth login window for the saved llm-gateway client; on success,
  // clears the sign-in alert and re-runs the BYOLLM connection test. On cancel
  // or failure, leaves the alert visible so the admin can retry.
  async function handleOAuthSignInClick(): Promise<void> {
    try {
      // Prefer the saved server-side client's configId (authoritative); fall
      // back to the unsaved site setting, then the legacy hardcode.
      const oauthConfigId =
        existingLlmGatewayClient?.oauthConfigId
        || formik.values.settings.siteSettingsV2?.onPremLLMOAuthConfigId
        || HARDCODED_LLM_GATEWAY_OAUTH_CONFIG_ID;
      await AddOAuthCredentialAction.attemptAuth(
        OAuthCredentialTypes.LlmGateway,
        undefined,
        undefined,
        oauthConfigId
      );
    } catch {
      return;
    }
    setNeedsOAuthSignIn(false);
    await refreshExistingLlmGatewayClient();
    // Show the spinner and keep the Test button disabled during the re-test, so a stray click
    // can't start an overlapping test.
    await withConnectionTestIndicator(() => runByollmConnectionTest());
  }

  function FeatureSection(config: {
    featureName: GAIFeature;
    testId: string;
    labelMessage: () => string;
    forceDisabled?: boolean;
    forceDisabledTooltipText?: string;
    // When set, the info icon becomes a click-triggered popover whose content is
    // forceDisabledTooltipText followed by a "Learn more" link to this URL. When
    // omitted, the info icon shows the plain hover tooltip instead.
    forceDisabledLearnMoreLink?: string;
  }): React.ReactElement {
    const fieldPath = GAI_FEATURE_FIELD_NAMES[config.featureName];
    const settingsToSearch =
      SITE_SETTINGS_V2_FEATURES.has(config.featureName) ? formik.values.settings.siteSettingsV2 : formik.values.settings;
    const isFeatureOn = settingsToSearch?.[fieldPath as keyof typeof settingsToSearch];

    // If the feature is on we always want to be able to turn it off
    // If the feature is off we want to be able to turn it on if the provider is
    // reachable AND every selected model passed its per-model test (BYOLLM may
    // fail one of three; OpenAI/Azure leaves areAllModelsValid as vacuous true).
    const isSectionEnabled = isFeatureOn || (isProviderReachable && areAllModelsValid);

    // `forceDisabled` hard-disables a feature that isn't supported for the current
    // provider/auth/license combination. The status icon reflects the disabled state
    // (never the green "on" check) even if the setting is still on server-side, but
    // the label copy keeps its normal styling so the row stays readable.
    const isButtonDisabled = config.forceDisabled || !isSectionEnabled;
    const isRowEnabled = isSectionEnabled && !config.forceDisabled;
    const showFeatureOnIcon = isFeatureOn && !config.forceDisabled;

    const buttonText = isFeatureOn ? Messages.onPremGAISiteSettingsTurnOff() : Messages.onPremGAISiteSettingsTurnOn();

    return (
      <div className={GenerativeAIOnPremSectionStyles.authoringContainer}>
        <div className={GenerativeAIOnPremSectionStyles.authoringIconLabel}>
          <div>
            {showFeatureOnIcon ? (
              <NotificationSuccessIcon size={14} />
            ) : (
              <ExcludeBaseIcon
                size={14}
                className={isRowEnabled ? undefined : GenerativeAIOnPremSectionStyles.authoringIconDisabled}
              />
            )}
          </div>
          <div style={GenerativeAIOnPremSectionStyles.getAuthoringLabelStyle(isSectionEnabled)}>{config.labelMessage()}</div>
        </div>
        <div className={GenerativeAIOnPremSectionStyles.featureButtonContainer}>
          {config.forceDisabled && (
            config.forceDisabledLearnMoreLink ? (
              <PopoverWidget
                testId={`${config.testId}-info-popover`}
                aria-describedby={`${config.testId}-info-popover-description`}
                placement='top'
                width={300}
                title={config.labelMessage()}
                popoverContent={
                  <p id={`${config.testId}-info-popover-description`}>
                    {config.forceDisabledTooltipText}
                    <TextLinkWidget
                      className={learnMoreStyle}
                      href={config.forceDisabledLearnMoreLink}
                      target='_blank'
                      testId={`${config.testId}-info-learn-more`}
                    >
                      {Messages.learnMore()}
                    </TextLinkWidget>
                  </p>
                }
                triggerElement={
                  <ButtonWidget
                    buttonType={ButtonType.IconOutline}
                    testId={`${config.testId}-info-button`}
                    title={Messages.moreInfoClickable()}
                    aria-label={Messages.moreInfoClickable()}
                    getContent={() => <NotificationInfoOutlineIcon size={16} alt={''} />}
                  />
                }
              />
            ) : (
              <Tooltip tooltipContent={config.forceDisabledTooltipText}>
                <NotificationInfoOutlineIcon size={16} />
              </Tooltip>
            )
          )}
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={config.testId}
            disabled={isButtonDisabled}
            onClick={() => {
              if (isFeatureOn) {
                clearFeature(config.featureName);
              } else {
                const hasSelectedModels = queryParsingModel || vizAndCalcGenerationModel || embeddingsModel;
                if (hasSelectedModels) {
                  formik.setFieldValue(getGAIFeatureSettingsPath(config.featureName), true);
                } else {
                  requestSelectModelsDialog(config.featureName);
                }
              }
            }}
          >
            {buttonText}
          </ButtonWidget>
        </div>
      </div>
    );
  }

  function Authoring(): React.ReactElement {
    return (
      <FeatureSection
        featureName={GAIFeature.Authoring}
        testId="tableau-gai-on-prem-authoring-button"
        labelMessage={Messages.tableauGAISiteSettingsFeatureAuthoring}
      />
    );
  }

  function DashboardCabu(): React.ReactElement {
    const isCabuDisabledForLicense = !isTableauAgentDashboardLicensed;
    const forceDisabled = isByollmOAuth || isCabuDisabledForLicense;
    // License gating takes precedence in the copy; the OAuth-not-supported case
    // additionally gets a "Learn more" documentation link in a click popover.
    const forceDisabledTooltipText = isCabuDisabledForLicense
      ? Messages.onPremGAISiteSettingsFeatureServerPlusRequiredTooltip()
      : Messages.onPremGAISiteSettingsFeatureOAuthNotSupportedTooltip();
    const forceDisabledLearnMoreLink = isCabuDisabledForLicense
      ? undefined
      : HelpService.getHelpUrlByCategory('gpt-server-models');

    return FeatureFlags.analyticsAssistantDashboardQASiteSettingOnPremEnabled ? (
      <FeatureSection
        featureName={GAIFeature.DashboardCabu}
        testId="tableau-gai-on-prem-cabu-button"
        labelMessage={Messages.tableauGAISiteSettingsForDashboardQA}
        forceDisabled={forceDisabled}
        forceDisabledTooltipText={forceDisabledTooltipText}
        forceDisabledLearnMoreLink={forceDisabledLearnMoreLink}
      />
    ) : null;
  }

  function Prep(): React.ReactElement {
    return (
      <FeatureSection
        featureName={GAIFeature.Prep}
        testId="tableau-gai-on-prem-prep-button"
        labelMessage={Messages.tableauGAISiteSettingsFeaturePrepFlow}
      />
    );
  }

  function AiSearch(): React.ReactElement {
    return FeatureFlags.ces_AISearchEnabled ? (
      <FeatureSection
        featureName={GAIFeature.AiSearch}
        testId="tableau-gai-on-prem-ai-search-button"
        labelMessage={Messages.tableauGAISiteSettingsFeatureAiSearch}
      />
    ) : null;
  }

  function ConnectButton(): React.ReactElement {
    return (
      <ButtonWidget buttonType={ButtonType.Outline} testId="connect-to-LLM-button" onClick={() => openConnectToLLMModal(false)}>
        {Messages.onPremGAISiteSettingsConnectButtonLabel()}
      </ButtonWidget>
    );
  }

  function APIKeyActionSection(): React.ReactElement {
    const defaultProvider = PROVIDER_TYPES.SELECT_PROVIDER;
    const selectedProvider = formik.values.settings.siteSettingsV2?.onPremLLMProvider || defaultProvider;
    const providerDisplayName = getProviderDisplayName(selectedProvider);

    return (
      <>
        <div className={GenerativeAIOnPremSectionStyles.apiKeyActionContainer}>
          <b>{providerDisplayName}</b>
          <div className={GenerativeAIOnPremSectionStyles.apiKeyButtonsContainer}>
            {/* @W-22921133: Temporarily hide Edit for BYOLLM + OAuth — admins must delete and recreate
                the connection instead. To be revisited when we test and verify the edit flow. */}
            {!isByollmOAuth && (
              <ButtonWidget buttonType={ButtonType.Outline} testId="edit-api-key-button" onClick={() => openConnectToLLMModal(true)}>
                {Messages.onPremGAISiteSettingsEditAPIKeyButtonLabel()}
              </ButtonWidget>
            )}
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId="test-api-key-button"
              // Disabled while a test is in flight (so the admin can't fire overlapping connection
              // tests by clicking repeatedly) or while the OAuth sign-in alert is up.
              disabled={isTestingConnection || needsOAuthSignIn}
              onClick={async () => {
                // Guard against overlapping runs; the disabled state above already blocks clicks, but
                // keep the in-flight state authoritative in case the button is triggered programmatically.
                if (isTestingConnection) {
                  return;
                }
                const currentApiKey = formik.values.settings.generativeAiLLMProviderAPIKey;
                const currentProvider = formik.values.settings.siteSettingsV2?.onPremLLMProvider;

                await withConnectionTestIndicator(() =>
                  currentProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM
                    ? runByollmConnectionTest()
                    : runOpenAIOrAzureTest(currentProvider, currentApiKey)
                );
              }}
            >
              {/* While a test is in flight, swap the label for an inline spinner. Rendering the spinner
                  as the button's own content keeps it in normal flow, unlike ButtonWidget's showSpinner
                  which overlays an absolutely-positioned spinner that can render blank in this layout. */}
              {isTestingConnection ? (
                <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId="test-api-key-button" />
              ) : (
                Messages.onPremGAISiteSettingsTestAPIKeyButtonLabel()
              )}
            </ButtonWidget>
            <ButtonWidget buttonType={ButtonType.Outline} testId="delete-api-key-button" onClick={() => openDeleteConfirmationModal()}>
              {Messages.onPremGAISiteSettingsDeleteAPIKeyButtonLabel()}
            </ButtonWidget>
          </div>
        </div>
      </>
    );
  }

  const hasAPIKey = !!formik.values.settings.generativeAiLLMProviderAPIKey;
  const hasHostURL = !!formik.values.settings.siteSettingsV2?.hostURL;
  const isOpenAIProvider = formik.values.settings.siteSettingsV2?.onPremLLMProvider === PROVIDER_TYPES.OPENAI;
  const isCustomGateway = formik.values.settings.siteSettingsV2?.onPremLLMProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM;

  const alertErrorDetail = alertMessage.errorDetail;
  const showDetailedErrorAlert =
    alertMessage.type === InlineAlertType.Error &&
    !!alertErrorDetail;

  // Only flag missing models when we have a fresh /models response to compare against —
  // if the fetch failed or hasn't run, availableModels stays null and we don't show a warning.
  // Limited to OpenAI: Azure/BYOLLM use free-text fields with no canonical list to validate against.
  const hasUnavailableSelectedModels = isOpenAIProvider && availableModels !== null && [
    queryParsingModel,
    vizAndCalcGenerationModel,
    embeddingsModel,
  ].some((model) => model.length > 0 && !availableModels.includes(model));

  const modelSettings = (
    <ModelSettingsSection
      isOnlineEnvironment={false}
      isConnectionValid={isProviderReachable}
      isOpenAIProvider={isOpenAIProvider}
      isCustomGateway={isCustomGateway}
      openSelectModelsDialog={() => requestSelectModelsDialog()}
      queryParsingModel={queryParsingModel}
      vizAndCalcGenerationModel={vizAndCalcGenerationModel}
      embeddingsModel={embeddingsModel}
      isDefaultOnlineModels={false}
      showEditModelsError={hasEditModelsError}
      dismissEditModelsError={() => setHasEditModelsError(false)}
      modelTestResults={modelTestResults}
      hasUnavailableSelectedModels={hasUnavailableSelectedModels}
    />
  );

  return (
    <SettingsSection
      title={Messages.tableauGAISiteSettingTitle()}
      testId={'generative-ai-on-prem-settings-section'}
      description={Messages.onPremGAISiteSettingIntroduction()}
    >
      <p>
        <b>{Messages.onPremGAISiteSettingsConnectToLLMLabel()}</b>
      </p>
      <p>{Messages.onPremGAISiteSettingsConnectToLLMInfo()}</p>

      {hasAPIKey || hasHostURL ? (
        <div className={GenerativeAIOnPremSectionStyles.apiKeyActionBackground}>
          <APIKeyActionSection />
        </div>
      ) : (
        <ConnectButton />
      )}

      {alertMessage.type && (
        <div className={GenerativeAIOnPremSectionStyles.alertContainer}>
          {showDetailedErrorAlert && alertErrorDetail ? (
            <InlineAlertWidget
              testId={'generative-ai-on-prem-alert'}
              type={alertMessage.type}
              className={TableauGaiSharedStyles.alertButtonRightAligned}
              button={
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  testId="error-details-button"
                  onClick={() => openErrorDetailsModal(alertErrorDetail)}
                >
                  {Messages.onPremGAIErrorDetailsButtonLabel()}
                </ButtonWidget>
              }
            >
              {alertMessage.text}
            </InlineAlertWidget>
          ) : (
            <CompactInlineAlertWidget
              testId={'generative-ai-on-prem-alert'}
              type={alertMessage.type}
              icon={alertMessage.dismissible !== false ? <ClearBaseIcon size={14} /> : undefined}
              onClick={alertMessage.dismissible !== false ? dismissAlert : undefined}
            >
              {alertMessage.text}
            </CompactInlineAlertWidget>
          )}
        </div>
      )}

      {needsOAuthSignIn && (
        <div className={GenerativeAIOnPremSectionStyles.alertContainer}>
          <InlineAlertWidget
            testId={'generative-ai-on-prem-oauth-signin-alert'}
            type={InlineAlertType.Warning}
            button={
              <ButtonWidget
                buttonType={ButtonType.Outline}
                testId="oauth-sign-in-button"
                onClick={handleOAuthSignInClick}
              >
                {Messages.onPremGAIOAuthSignInButtonLabel()}
              </ButtonWidget>
            }
          >
            {Messages.onPremGAIOAuthSignInRequiredMessage()}
          </InlineAlertWidget>
        </div>
      )}

      {modelSettings}

      <p className={GenerativeAIOnPremSectionStyles.sectionLabel}>
        <b>{Messages.onPremGAISiteSettingsByFeatureLabel()}</b>
      </p>
      <p>{Messages.onPremGAISiteSettingsByFeatureDescription()}</p>

      {/* The table and the standalone toggles below cover the same four features
          (webAuthoring, dashboardInsights, prep, tableauAiSearch), so exactly one is shown:
          the table when agentPermissionsTableOnPremEnabled is on, the toggles otherwise. */}
      {FeatureFlags.agentPermissionsTableOnPremEnabled && (
        <AgentPermissionsTable
          allowTestRegistration={!FeatureFlags.tabGAITokenServiceRegistration}
          formik={formik}
          isTrialSite={false}
          isTryItMode={false}
          registered={true}
          isOnline={false}
          // Keep the table's actions disabled until the LLM connection and the selected models
          // have both validated. isProviderReachable covers the connection/models fetch;
          // areAllModelsValid covers the per-model tests (vacuous true for OpenAI/Azure, real for BYOLLM).
          connectionValid={isProviderReachable && areAllModelsValid}
          onRequestEnable={handleTableRequestEnable}
          isEnableForceDisabled={isTableEnableForceDisabled}
        />
      )}
      {!FeatureFlags.agentPermissionsTableOnPremEnabled && <Authoring />}
      {!FeatureFlags.agentPermissionsTableOnPremEnabled && <DashboardCabu />}
      {!FeatureFlags.agentPermissionsTableOnPremEnabled && <Prep />}
      {!FeatureFlags.agentPermissionsTableOnPremEnabled && <AiSearch />}

      {selectModelsDialogProps && <SelectModelsDialog {...selectModelsDialogProps} />}
    </SettingsSection>
  );
}

export const GenerativeAISectionOnPremSpecs = componentRegistryEntry({
  name: 'GenerativeAISectionOnPrem',
  component: GenerativeAISectionOnPrem,
  transformSettingsBeforeSubmit: (settings) => {
    // Don't save the default placeholder API key value
    if (settings.generativeAiLLMProviderAPIKey === GAI_DEFAULT_API_KEY_PLACEHOLDER) {
      return {
        ...settings,
        generativeAiLLMProviderAPIKey: null,
      };
    }
    return settings;
  },
});
